import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import Axios from "helpers/Interceptor";
import {
    CampaignDetails,
    TargetingAudienceGroup,
    InstagramPost,
    TargetedLanguage,
    PlaybookStep,
    TikTokSparkAd,
    TikTokTargetingGeography,
    SearchPlatformInterestsResult,
    ExternalLink,
    RecordDetails,
    MediaType,
    MediaProductType,
    YouTubeCampaignData,
    PlaylistingCampaignInput,
    FBLocation,
    TargetedGender,
    CurrencyCodesType,
    DefaultPixelConversionEventNames
} from 'modules/Types';
import {
    getCampaign_api,
    createOneOffCampaignCheckout_v2_api,
    createUpgradePlanCampaignCheckout_v2_api,
    createDirectFacebookCampaign_api,
    createTiktokCampaign_v2_api,
    getConvertedCurrency_api
} from 'api/campaignApi';
import { PlaybookType, PlaybookStepName, DEFAULT_CURRENCY, MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS, MINIMUM_LIFETIME_BUDGET_TIKTOK, PLAYBOOK_TYPE_INFO, AdPlatform } from 'modules/Const';
import { getAvailableTargetingLocations_api } from 'api/tiktokApi';
import useCurrentBrand, { BrandExistsState } from './useCurrentBrand';
import {
    CallToActionType,
    FacebookCampaignSubmissionType,
    FB_CAMPAIGNS_DEFAULT_LANGUAGES,
    FB_DEFAULT_GENDERS,
    FB_DEFAULT_MAX_AGE,
    FB_DEFAULT_MIN_AGE,
    TargetedAudience,
    TargetedAudienceType,
    TikTokCampaignSubmissionType,
    YouTubeCampaignSubmissionType,
    transformDataForCampaignSubmit,
} from 'modules/Campaign';
import { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import useFacebookConnections from './useFacebookConnections';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { getOneClickCampaignDetails_api, ImportFromURLResponse } from 'api/agentApi';
import { searchGeographiesFromFacebook } from 'api/facebookApi';
import dayjs from 'dayjs';
import { SHARED_AUDIENCE_CONFIGS } from 'modules/Const';
import useTikTokConnections from './useTikTokConnections';
import { track } from 'analytics';
import { isEmpty } from 'modules/Utils';

export const CURRENT_BUDGET_TABS = {
    LIFETIME: 'lifetime',
    DAILY: 'daily'
}

export const CURRENT_DATE_SELECTION_TABS = {
    DATE_RANGE: 'dateRange',
    NUMBER_OF_DAYS: 'numberOfDays',
    MAXIMUM_BUDGET: 'maximumBudget'
}

/** Default geography configuration for primary markets */
const DEFAULT_PRIMARY_MARKET_GEOGRAPHIES = [
    {
        id: 'US',
        name: 'United States',
        label: 'United States (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'GB',
        name: 'United Kingdom',
        label: 'United Kingdom (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'CA',
        name: 'Canada',
        label: 'Canada (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'FR',
        name: 'France',
        label: 'France (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'DE',
        name: 'Germany',
        label: 'Germany (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'AU',
        name: 'Australia',
        label: 'Australia (country)',
        type: 'country' as const,
        radius: 0,
    },
];

/** Default geography configuration for global markets */
const DEFAULT_GLOBAL_MARKET_GEOGRAPHIES = [
    {
        id: 'TR',
        name: 'Turkey',
        label: 'Turkey (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'IT',
        name: 'Italy',
        label: 'Italy (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'PL',
        name: 'Poland',
        label: 'Poland (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'SE',
        name: 'Sweden',
        label: 'Sweden (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'NO',
        name: 'Norway',
        label: 'Norway (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'MX',
        name: 'Mexico',
        label: 'Mexico (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'NL',
        name: 'Netherlands',
        label: 'Netherlands (country)',
        type: 'country' as const,
        radius: 0,
    },
    {
        id: 'PH',
        name: 'Philippines',
        label: 'Philippines (country)',
        type: 'country' as const,
        radius: 0,
    },
];

/** Represents a single geography bucket with its locations and budget */
export interface TargetedGeographyBucket {
    id: number;
    name: string;
    percentage: number;
    geographies: {
        id: string;
        name: string;
        label: string;
        type: 'country' | 'region' | 'city';
        radius?: number;
        country_code?: string
        country_name?: string
        region?: string
        region_id?: number
        supports_city?: boolean
    }[];
    excludedGeographies?: {
        id: string;
        name?: string;
        label?: string;
        type: 'country' | 'region' | 'city';
        radius?: number;
        country_code?: string
        country_name?: string
        region?: string
        region_id?: number
        supports_city?: boolean
    }[];
};
export interface CreativeSelectorPost {
    disabled?: boolean;
    originalDescription?: string;
    /** Unique identifier for the post */
    id: string;
    /** Caption or title text for the post */
    title: string;
    /** Timestamp when post was created */
    timestamp?: string;
    /** Whether post is recommended by AI */
    isRecommended?: boolean;
    /** Original post type */
    originalPostType?: AssetPlatformType | undefined;
    /** Whether post is a carousel */
    username?: string;
    /** Media type of the post */
    mediaType?: MediaType | undefined;
    /** Direct URL to video content if available */
    assetUrl?: string;
    /** Thumbnail URL specifically for videos */
    thumbnailUrl?: string;
    /** Caption for the post from the platform */
    platformCaption?: string;
    /** Descriptive text providing more information about the audience */
    description?: string;
    /** Array of tag strings associated with this audience */
    tags?: string[];
    /** Instagram ID */
    ig_id?: string;
    /** Facebook ID */
    fb_id?: string;
    /** Maximum number of items that can be selected */
    maxSelectedItems?: number;
    /** Like count */
    likesCount?: number;
    /** Comment count */
    commentsCount?: number;
    /** Share count */
    sharesCount?: number;
    /** Background color */
    backgroundColor?: string;
    /** Child images for carousel posts */
    children?: {
        id: string;
        mediaType: MediaType;
        mediaUrl: string;
    }[];
    /** Current index for carousel display */
    currentCarouselIndex?: number;
}

export type CurrentDateSelectionTabType = typeof CURRENT_DATE_SELECTION_TABS[keyof typeof CURRENT_DATE_SELECTION_TABS];

export const DEFAULT_TIKTOK_GEOGRAPHIES: TargetedGeographyBucket[] = [
    {
        id: 1,
        name: "Primary Targeting",
        percentage: 100,
        geographies: [
            {
                id: "6252001",
                name: "United States",
                label: "United States (Country)",
                type: "country",
                country_code: "US",
                country_name: "United States",
                region: "United States",
                region_id: 1,
                supports_city: false
            },
            {
                id: "6251999",
                name: "Canada",
                label: "Canada (Country)",
                type: "country",
                country_code: "CA",
                country_name: "Canada",
                region: "Canada",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2635167",
                name: "United Kingdom",
                label: "United Kingdom (Country)",
                type: "country",
                country_code: "UK",
                country_name: "United Kingdom",
                region: "United Kingdom",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2077456",
                name: "Australia",
                label: "Australia (Country)",
                type: "country",
                country_code: "AU",
                country_name: "Australia",
                region: "Australia",
                region_id: 1,
                supports_city: false
            },
            {
                id: "3017382",
                name: "France",
                label: "France (Country)",
                type: "country",
                country_code: "FR",
                country_name: "France",
                region: "France",
                region_id: 1,
                supports_city: false
            },
            {
                id: "2921044",
                name: "Germany",
                label: "Germany (Country)",
                type: "country",
                country_code: "DE",
                country_name: "Germany",
                region: "Germany",
                region_id: 1,
                supports_city: false
            }
        ]
    }
]

/** Interface defining campaign goal configuration */
export interface CampaignGoal {
    /** Unique identifier for the goal */
    id: string;
    /** Display label for the goal */
    label: string;
    /** Icon identifier for visual representation */
    icon: string;
    /** Detailed description of what the goal achieves */
    description: string;
    /** Recommended usage of this goal */
    recommendation: string;
    /** Specific conversion goal identifier */
    conversionGoal: string;
    /** Helpful tips for achieving this goal */
    tips: string;
    /** Optional array of use cases for this goal */
    whenToUse?: string[];
    /** Optional array of required setup steps */
    setupNeeded?: string[];
    /** Type of conversion tracking */
    conversionType: string;
    /** Expected average cost per conversion */
    averageConversionCost: number;
    /** Whether this goal is currently available */
    available?: boolean;
    /** Note explaining availability status if applicable */
    availabilityNote?: string;
    /** Whether this goal is currently available */
    subtype?: string;
}

export type Caption = {
    id: number;
    text: string;
    aiGenerated?: boolean;
}

/** Type for budget tab options */
export type CurrentBudgetTabType = typeof CURRENT_BUDGET_TABS[keyof typeof CURRENT_BUDGET_TABS];

/** Interface defining the campaign state and actions */
export interface CampaignState {
    /** Campaign daily budget in USD */
    budget: string;
    /** Campaign maximum budget in USD */
    budgetMaximum: string;
    /** Campaign start date in ISO format */
    startDate: string | null;
    /** Campaign end date in ISO format */
    endDate: string | null;
    /** Type of playbook being used */
    playbookType: PlaybookType | null;
    /** Type of ad platform being used */
    adPlatform: AdPlatform | null;
    /** Current campaign details */
    campaign: CampaignDetails | null;

    showOneClickCampaignModal: boolean;
    showOneClickCampaignSetupModal: boolean;
    showCreateNewCampaignModal: boolean;
    newCampaignAdPlatformFilter: AdPlatform | null;
    captions: Caption[];
    /** User-defined campaign name */
    campaignName: string;
    /** Loading state for campaign operations */
    isLoadingCampaign: boolean;
    /** Loading state for campaign submission */
    isSubmittingCampaign: boolean;
    isLoadingSocialItems: boolean;
    /** Total duration of campaign in days */
    numberOfDays: number;
    /** Selected campaign goal configuration */
    campaignGoal: CampaignGoal | null;
    /** Current step in the playbook setup process */
    currentStep: PlaybookStepName | undefined;
    /** Setup mode selection */
    expressSetupOrCustomPages: 'custom' | 'express';
    /** Selected call-to-action value for TikTok ads */
    callToAction: CallToActionType;
    selectedInstagramPosts: InstagramPost[]
    uploadedAssets: CloudinaryUploadedFileType[]
    /** Selected spark codes for TikTok ads */
    selectedSparkCodes: TikTokSparkAd[];
    geographyBuckets: TargetedGeographyBucket[];
    setGeographyBuckets: (geographyBuckets: TargetedGeographyBucket[]) => void;
    showEditGeographyBucketsBanner: boolean;
    setShowEditGeographyBucketsBanner: (show: boolean) => void;
    isGeographyBucketsObjectValid: boolean;
    validateGeographyBuckets: () => void;
    /** Loading state for geography search */
    isLoadingGeographies: boolean;
    /** Selected audience groups for campaign targeting */
    selectedAudienceGroups: TargetingAudienceGroup[];
    /** Minimum age for campaign targeting */
    minAge: number;
    /** Maximum age for campaign targeting */
    maxAge: number;

    /** Gender targeting setting */
    genders: TargetedGender[];
    /** Selected language locales for targeting */
    languages: TargetedLanguage[];
    /** Whether demographics targeting is automated */
    isDemographicsAutomated: boolean;
    /** Previewed asset for TikTok ads */
    previewedAsset: CreativeSelectorPost | null;
    /** Current budget tab */
    currentBudgetTab: CurrentBudgetTabType;
    /** Current date selection tab */
    currentDateSelectionTab: CurrentDateSelectionTabType;
    isGeneratingOneClickCampaign: boolean;
    campaignDisclaimer: string;

    selectedSongToPromote: RecordDetails | null;
    highlightedTrack: {
        id: string;
        uri: string;
        name: string;
    } | null;
    isLoadingMaestroRecommendations: boolean;

    playbookStepNames: PlaybookStepName[];

    linksToPromote: ExternalLink[];
    enabledPlatforms: {
        url: string;
        name: string;
    }[]
    conversionsEnabled: boolean;
    conversionsEventName?: string;

    instagramPosts: InstagramPost[];
    releases: RecordDetails[];
    tikTokPosts: any[];
    youTubeVideos: any[];
    importedFromUrlPosts: ImportFromURLResponse[];
    campaignInitialized: boolean;

    // Actions
    /** Updates the campaign budget */
    setBudget: (budget: string) => void;
    /** Updates the campaign maximum budget */
    setBudgetMaximum: (budgetMaximum: string) => void;
    setCaptions: (captions: Caption[]) => void;
    updateCaption: (captionId: number, text: string) => void;
    /** Updates the campaign goal */
    setCampaignGoal: (goal: CampaignGoal | null) => void;
    /** Updates the campaign start date */
    setStartDate: (startDate: string) => void;
    /** Updates the campaign end date */
    setEndDate: (endDate: string) => void;
    /** Updates the playbook type */
    setPlaybookType: (playbookType: PlaybookType) => void;
    /** Updates the ad platform */
    setAdPlatform: (adPlatform: AdPlatform | null) => void;
    /** Updates the loading state */
    setIsLoadingCampaign: (isLoadingCampaign: boolean) => void;
    setIsLoadingSocialItems: (isLoadingSocialItems: boolean) => void;
    /** Updates the loading state for campaign submission */
    setIsSubmittingCampaign: (isSubmittingCampaign: boolean) => void;
    /** Updates the previewed asset */
    setPreviewedAsset: (asset: CreativeSelectorPost | null, chooseNextAsset?: boolean) => void;
    /** Updates the selected audience groups */
    setSelectedAudienceGroups: (audienceGroups: TargetingAudienceGroup[]) => void;
    /** Removes a tag from an audience group */
    removeInterestFromAudienceGroup: (audienceGroupId: string, interestId: number) => void;
    /** Adds a tag to an audience group */
    addInterestToAudienceGroup: (audienceGroupId: string, interest: SearchPlatformInterestsResult) => void;
    /** Removes an audience group */
    removeAudienceGroup: (audienceGroupId: string) => void;
    /** Updates the campaign duration in days */
    setNumberOfDays: (numberOfDays: number) => void;
    /** Updates the campaign details */
    setCampaign: (campaign: CampaignDetails) => void;
    /** Updates the show one click campaign modal */
    setShowOneClickCampaignModal: (show: boolean) => void;
    /** Updates the show one click campaign setup modal */
    setShowOneClickCampaignSetupModal: (show: boolean) => void;
    /** Updates the show create new campaign modal */
    setShowCreateNewCampaignModal: (show: boolean) => void;
    setNewCampaignAdPlatformFilter: (adPlatform: AdPlatform | null) => void;
    /** Updates the campaign name */
    setCampaignName: (campaignName: string) => void;
    /** Fetches campaign details by ID */
    getCampaign: (campaignId: number) => Promise<CampaignDetails | null>;
    /** Creates a new campaign for a brand */
    createCampaign: (brandId: string) => Promise<CampaignDetails>;
    /** Updates the current playbook step */
    setCurrentStep: (step: PlaybookStepName) => void;
    /** Updates the playbook step names */
    setPlaybookStepNames: (playbookStepNames: PlaybookStepName[]) => void;
    /** Updates the setup mode */
    setExpressSetupOrCustomPages: (value: 'custom' | 'express') => void;
    /** Updates the selected call-to-action */
    setCallToAction: (callToAction: CallToActionType) => void;
    /** Updates the selected spark codes */
    setSelectedSparkCodes: (sparkCodes: TikTokSparkAd[]) => void;
    /** Adds a spark code to the selected spark codes */
    addSparkCode: (sparkCode: TikTokSparkAd) => boolean;
    /** Removes a specific spark code */
    removeSparkCode: (sparkCodeId: string) => void;
    /** Searches for available geographies */
    searchGeographies: (query: string, platform: 'tiktok' | 'facebook') => Promise<TikTokTargetingGeography[] | FBLocation[]>;
    /** Updates the minimum targeting age */
    setMinAge: (age: number) => void;
    /** Updates the maximum targeting age */
    setMaxAge: (age: number) => void;
    /** Updates both minimum and maximum age */
    setAgeRange: (minAge: number, maxAge: number) => void;
    /** Updates the gender targeting setting */
    setTargetedGender: (gender: TargetedGender[]) => void;
    /** Updates the language locales */
    setLanguages: (locales: TargetedLanguage[]) => void;
    /** Updates the demographics automation setting */
    setIsDemographicsAutomated: (isAutomated: boolean) => void;
    /** Gets all selected posts in the transformed format */
    getAllSelectedPosts: (variant: 'facebook' | 'tiktok') => CreativeSelectorPost[];
    /** Updates multiple demographics settings */
    setDemographics: (demographics: Partial<{
        minAge: number;
        maxAge: number;
        genders: TargetedGender[];
        locales: TargetedLanguage[];
        isAutomated: boolean;
    }>) => void;

    setIsLoadingMaestroRecommendations: (isLoading: boolean) => void;

    /** Updates the current budget tab */
    setCurrentBudgetTab: (tab: CurrentBudgetTabType) => void;
    /** Updates the current date selection tab */
    setCurrentDateSelectionTab: (tab: CurrentDateSelectionTabType) => void;
    /** Updates the playbook steps after finalization */
    setPlaybookSteps: (steps: PlaybookStep[]) => void;
    /** Updates the link to promote */
    setLinksToPromote: (links: ExternalLink[]) => void;
    /** Updates the enabled platforms */
    setEnabledPlatforms: (platforms: {
        url: string;
        name: string;
    }[]) => void;

    setSelectedSongToPromote: (songToPromote: RecordDetails) => void;
    setHighlightedTrack: (track: {
        id: string;
        uri: string;
        name: string;
    }) => void;
    getDataForDebugBox: () => Record<string, any>;

    nextCampaignStep: () => void;
    prevCampaignStep: () => void;
    setSelectedInstagramPosts: (posts: InstagramPost[]) => void;
    removeSelectedInstagramPost: (postId: string) => void;
    setUploadedAssets: (assets: CloudinaryUploadedFileType[]) => void;
    getUploadedAssets: () => CloudinaryUploadedFileType[];
    removeUploadedAsset: (assetId: string) => void;
    setImportedFromUrlPosts: (posts: ImportFromURLResponse[]) => void;
    removeImportedFromUrlPost: (postId: string) => void;
    submitCampaign: ({
        submitType,
        recurring_interval,
        overrideCampaignInput
    }: {
        submitType: 'oneOffCheckout' | 'upgradePlanCheckout' | 'directCreate',
        recurring_interval?: BillingCycle,
        overrideCampaignInput?: YouTubeCampaignData | PlaylistingCampaignInput
    }) => Promise<any>;
    resetDemographicsToRecommended: () => void;
    resetAudiencesToRecommended: () => void;
    getDataForSubmission: () => FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | YouTubeCampaignData;
    campaignAllowsDirectPayment: () => boolean;
    campaignDoesntRequireUpgrade: () => boolean;
    /** Resets all state values to their defaults */
    resetCampaignState: () => void;
    initializeCampaignState: ({
        playbookType,
        currentStep,
        playbookStepNames
    }: {
        playbookType: PlaybookType;
        currentStep?: PlaybookStepName;
        playbookStepNames?: PlaybookStepName[];
    }) => void;
    setConversionsEnabled: (enabled: boolean) => void;
    setConversionsEventName: (event: string) => void;
    /** Preserves existing audience groups when initializing campaign state */
    preserveLoadedAudienceGroups: (preserveExisting?: boolean) => TargetingAudienceGroup[];
    /** Resets geography buckets to default values */
    resetGeographyBuckets: (multipleLocations?: boolean) => void;
    /** Returns campaign data in format needed for submission */

    getInstagramPosts: () => Promise<InstagramPost[]>;
    getReleases: () => Promise<RecordDetails[]>;
    getTikTokPosts: () => Promise<any[]>;
    getYouTubeVideos: () => Promise<any[]>;

    selectedInstagramPost: InstagramPost | null;
    selectedRelease: RecordDetails | null;
    selectedTikTokPost: any | null;
    selectedYouTubeVideo: any | null;
    setSelectedInstagramPost: (post: InstagramPost | null) => void;
    setSelectedRelease: (release: RecordDetails | null) => void;
    setSelectedTikTokPost: (post: any | null) => void;
    setSelectedYouTubeVideo: (video: any | null) => void;

    isBudgetTooLow: boolean;
    generateOneClickCampaign: () => Promise<void>;
    checkIsAdBudgetTooLow: () => Promise<void>;
    getTotalBudget: () => {
        totalBudget: number;
        isValid: boolean;
        budgetType: string;
    };
}

export interface CampaignExistsState extends CampaignState {
    playbookType: PlaybookType;
    adPlatform: AdPlatform;
}

/** Campaign management hook using Zustand store */
const useCampaign = create(
    immer<CampaignState>((set, get) => ({
        budget: '100',
        budgetMaximum: '100',
        startDate: '',
        endDate: '',
        // playbookType: PlaybookType.INCREASE_MUSIC_STREAMS,
        playbookType: null,
        adPlatform: null,
        campaign: null,
        showOneClickCampaignModal: false,
        showOneClickCampaignSetupModal: false,
        campaignName: '',
        isLoadingCampaign: false,
        isSubmittingCampaign: false,
        isLoadingSocialItems: false,
        highlightedTrack: null,
        numberOfDays: 0,
        selectedInstagramPosts: [],
        campaignInitialized: false,
        captions: [],
        showCreateNewCampaignModal: false,
        newCampaignAdPlatformFilter: AdPlatform.META,
        campaignDisclaimer: '',
        importedFromUrlPosts: [],
        setCaptions: (captions: Caption[]) => { set(state => { state.captions = captions }) },
        updateCaption: (captionId: number, text: string) => { set(state => { state.captions = state.captions.map(c => c.id === captionId ? { ...c, caption: text } : c) }) },
        uploadedAssets: [],
        currentStep: undefined,
        expressSetupOrCustomPages: 'custom',
        callToAction: { value: 'LEARN_MORE', label: 'Learn More' },
        selectedSparkCodes: [],
        geographyBuckets: [{
            id: 1,
            name: '',
            percentage: 100,
            geographies: []
        }],
        showEditGeographyBucketsBanner: false,
        isGeographyBucketsObjectValid: true,
        isLoadingGeographies: false,
        targetedGeographies: [],
        campaignGoal: null,
        isGeneratingOneClickCampaign: false,
        minAge: 18,
        selectedAudienceGroups: [],
        maxAge: 65,
        genders: [],
        languages: [],
        isDemographicsAutomated: true,
        previewedAsset: null,
        currentBudgetTab: CURRENT_BUDGET_TABS.LIFETIME,
        currentDateSelectionTab: CURRENT_DATE_SELECTION_TABS.DATE_RANGE,
        playbookStepNames: [],
        linksToPromote: [],
        conversionsEnabled: false,
        conversionsEventName: undefined,
        instagramPosts: [],
        releases: [],
        tikTokPosts: [],
        youTubeVideos: [],

        selectedInstagramPost: null,
        selectedRelease: null,
        selectedTikTokPost: null,
        selectedYouTubeVideo: null,
        isBudgetTooLow: false,
        /** for conversions campaigns, this is the platforms that are enabled 
        * on the link
        */
        setConversionsEventName: (event: string) => { set(state => { state.conversionsEventName = event }) },
        setConversionsEnabled: (enabled: boolean) => {
            const { selectedAudienceGroups, setSelectedAudienceGroups, adPlatform } = get();
            const { selectedFacebookPixel } = useFacebookConnections.getState();

            set(state => { state.conversionsEnabled = enabled });

            // Only sync audiences for Facebook ad campaigns
            if (adPlatform !== AdPlatform.META) {
                return
            };

            // Handle conversion audience groups
            const conversionEventLookalikesAudienceId = SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES.id;

            if (enabled && selectedFacebookPixel?.id) {
                const audienceExists = selectedAudienceGroups.some(group =>
                    group.id === conversionEventLookalikesAudienceId
                );

                // Add conversion lookalikes if not already present
                if (!audienceExists) {
                    setSelectedAudienceGroups([
                        ...selectedAudienceGroups,
                        SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES
                    ]);
                }
            } else {
                // Remove conversion lookalikes if conversions disabled or no pixel connected
                const updatedGroups = selectedAudienceGroups.filter(group =>
                    group.id !== conversionEventLookalikesAudienceId
                );

                // Only update if something was removed
                if (updatedGroups.length !== selectedAudienceGroups.length) {
                    setSelectedAudienceGroups(updatedGroups);
                }
            }
        },
        enabledPlatforms: [],

        selectedSongToPromote: null,
        isLoadingMaestroRecommendations: false,

        campaignAllowsDirectPayment: () => {
            const { playbookType, expressSetupOrCustomPages } = get();
            return playbookType === PlaybookType.INCREASE_VIDEO_VIEWS || playbookType === PlaybookType.GET_PLAYLISTED || expressSetupOrCustomPages === 'express'
        },

        campaignDoesntRequireUpgrade: () => {
            const { playbookType } = get();
            return playbookType === PlaybookType.GET_PLAYLISTED;
        },

        removeInterestFromAudienceGroup: (audienceGroupId: string, interestId: number) => {
            set(state => {
                state.selectedAudienceGroups = state.selectedAudienceGroups.map(group => {
                    if (group.id === audienceGroupId) {
                        group.tags = group.tags.filter(tag => tag.id !== interestId);
                    }
                    return group;
                });
            });
        },
        setLinksToPromote: (links: ExternalLink[]) => { set(state => { state.linksToPromote = links }) },
        setEnabledPlatforms: (platforms: {
            url: string;
            name: string;
        }[]) => { set(state => { state.enabledPlatforms = platforms }) },
        addInterestToAudienceGroup: (audienceGroupId: string, interest: SearchPlatformInterestsResult) => {
            set(state => {
                // Check if audience group exists
                const groupExists = state.selectedAudienceGroups.some(group => group.id === audienceGroupId);

                if (!groupExists) {
                    // Create new audience group if it doesn't exist
                    // Convert id from camelCase to title case for display
                    const title = audienceGroupId
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase());

                    state.selectedAudienceGroups.push({
                        id: audienceGroupId,
                        title,
                        tags: []
                    });
                }

                // Add interest to the group
                state.selectedAudienceGroups = state.selectedAudienceGroups.map(group => {
                    if (group.id === audienceGroupId) {
                        group.tags = [...group.tags, {
                            id: Number(interest.id),
                            title: interest.name,
                            audience_size: interest.audience_size,
                            type: interest.type as TargetedAudienceType
                        }];
                    }
                    return group;
                });
            });
        },

        removeAudienceGroup: (audienceGroupId: string) => {
            set(state => {
                state.selectedAudienceGroups = state.selectedAudienceGroups.filter(group => group.id !== audienceGroupId);
            });
        },

        checkIsAdBudgetTooLow: async (): Promise<void> => {
            const {
                budget,
                startDate,
                endDate,
                numberOfDays,
                currentBudgetTab,
                currentDateSelectionTab,
                getTotalBudget,
                playbookType,
            } = get();

            const {
                selectedFacebookAdAccount
            } = useFacebookConnections.getState();

            const budgetNumber = Number(budget);

            if (isEmpty(budget) || isNaN(budgetNumber) || budgetNumber <= 0) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }

            if (playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
                set(state => { state.isBudgetTooLow = budgetNumber < MINIMUM_LIFETIME_BUDGET_TIKTOK });
                return;
            }

            const adPlatformCurrency = selectedFacebookAdAccount?.currency || DEFAULT_CURRENCY;

            const isDefaultCurrency = adPlatformCurrency === DEFAULT_CURRENCY;

            // Minimum budget requirements based on playbook type
            // Same minimum daily budget for all ad types
            const minimumDailyBudget = isDefaultCurrency ?
                MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS
                :
                await getConvertedCurrency_api({
                    from: 'USD',
                    to: adPlatformCurrency as CurrencyCodesType,
                    amount: MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS
                });

            // For daily budget tab, just compare to minimum daily
            if (currentBudgetTab === CURRENT_BUDGET_TABS.DAILY) {
                set(state => { state.isBudgetTooLow = budgetNumber < minimumDailyBudget });
                return;
            }

            // For total budget tab, calculate the daily equivalent based on date selection method
            let calculatedDurationDays = 0;

            // Calculate duration based on the date selection method
            if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.DATE_RANGE) {
                // If no dates selected, can't validate
                if (!startDate || !endDate) {
                    set(state => { state.isBudgetTooLow = true });
                    return;
                }

                // Calculate duration in days
                const startDateObj = dayjs(startDate).format('YYYY-MM-DD');
                const endDateObj = dayjs(endDate).format('YYYY-MM-DD');
                calculatedDurationDays = dayjs(endDateObj).diff(dayjs(startDateObj), 'days');
            } else if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS) {
                // Use the specified number of days
                calculatedDurationDays = numberOfDays;
            } else if (currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET) {
                // For maximum budget, we need to ensure daily budget is at least minimum
                const maxDays = Math.floor(budgetNumber / minimumDailyBudget);

                // If budget can't even support one day at minimum rate, it's invalid
                set(state => { state.isBudgetTooLow = maxDays < 1 });
                return
            }

            // If we couldn't determine duration or it's invalid, budget is invalid
            if (calculatedDurationDays <= 0) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }

            // For TikTok, check minimum lifetime budget of $50
            const isTikTok = PLAYBOOK_TYPE_INFO[playbookType as PlaybookType]?.adPlatform === AdPlatform.TIKTOK;
            if (isTikTok && budgetNumber < MINIMUM_LIFETIME_BUDGET_TIKTOK) {
                set(state => { state.isBudgetTooLow = true });
                return;
            }

            // Calculate daily budget based on total budget and duration
            const dailyBudget = budgetNumber / calculatedDurationDays;

            // if the users budget isn't big enough to add multiple locations, we need to reset the geography buckets to a single location
            const {
                isValid: canAddMultipleLocations,
            } = getTotalBudget();

            // if (!canAddMultipleLocations) {
            //     resetGeographyBuckets(false);
            // }

            set(state => { state.isBudgetTooLow = dailyBudget < minimumDailyBudget });
        },

        getTotalBudget: (): {
            totalBudget: number;
            isValid: boolean;
            budgetType: string;
        } => {
            const {
                budget,
                currentBudgetTab,
            } = get();

            const LIFETIME_BUDGET_THRESHOLD = 100;
            const DAILY_BUDGET_THRESHOLD = 10;
            const budgetNumber = Number(budget);

            // If budget is not a valid number, it's invalid
            if (!budget || budget === '' || isNaN(budgetNumber) || budgetNumber <= 0) {
                return {
                    totalBudget: 0,
                    isValid: false,
                    budgetType: currentBudgetTab
                };
            }

            // Case 1: Daily budget
            if (currentBudgetTab === CURRENT_BUDGET_TABS.DAILY) {
                return {
                    totalBudget: budgetNumber,
                    isValid: budgetNumber > DAILY_BUDGET_THRESHOLD,
                    budgetType: 'daily'
                };
            }

            // Case 2: Lifetime budget
            if (currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME) {
                // Calculate total lifetime budget based on the selected date option
                let totalLifetimeBudget = budgetNumber;

                // If using number of days, we could calculate total budget as daily × days
                // but since we already have the lifetime budget directly, we just use it

                return {
                    totalBudget: totalLifetimeBudget,
                    isValid: totalLifetimeBudget >= LIFETIME_BUDGET_THRESHOLD,
                    budgetType: 'lifetime'
                };
            }

            // Default fallback (should not reach here in normal operation)
            return {
                totalBudget: budgetNumber,
                isValid: false,
                budgetType: currentBudgetTab
            };
        },

        setSelectedAudienceGroups: (audienceGroups: TargetingAudienceGroup[]) => { set(state => { state.selectedAudienceGroups = audienceGroups }) },

        setPreviewedAsset: (asset: CreativeSelectorPost | null, chooseNextAsset: boolean = true) => {
            const {
                uploadedAssets,
                selectedInstagramPosts,
                selectedSparkCodes,
                previewedAsset
            } = get();

            if (asset === null) {
                // If we want to choose the next asset
                if (chooseNextAsset) {
                    // Try to get first uploaded asset or instagram post
                    const nextAsset = uploadedAssets[0] || selectedInstagramPosts[0] || null;

                    if (nextAsset) {
                        const transformedPost = transformPostsToCreativeFormat([nextAsset])[0];
                        set(state => { state.previewedAsset = transformedPost });
                        return;
                    }
                }

                set(state => { state.previewedAsset = null });
                return;
            }

            set(state => { state.previewedAsset = asset });
        },
        setCampaignGoal: (goal: CampaignGoal | null) => { set(state => { state.campaignGoal = goal }) },
        setBudget: (budget: string) => { set(state => { state.budget = budget }) },
        setBudgetMaximum: (budgetMaximum: string) => { set(state => { state.budgetMaximum = budgetMaximum }) },
        setStartDate: (startDate: string) => { set(state => { state.startDate = startDate }) },
        setEndDate: (endDate: string) => { set(state => { state.endDate = endDate }) },
        setPlaybookType: (playbookType: PlaybookType) => { set(state => { state.playbookType = playbookType }) },
        setAdPlatform: (adPlatform: AdPlatform | null) => { set(state => { state.adPlatform = adPlatform }) },
        setIsLoadingCampaign: (isLoadingCampaign: boolean) => { set(state => { state.isLoadingCampaign = isLoadingCampaign }) },
        setIsSubmittingCampaign: (isSubmittingCampaign: boolean) => { set(state => { state.isSubmittingCampaign = isSubmittingCampaign }) },
        setIsLoadingSocialItems: (isLoadingSocialItems: boolean) => { set(state => { state.isLoadingSocialItems = isLoadingSocialItems }) },
        setCampaign: (campaign: CampaignDetails) => { set(state => { state.campaign = campaign }) },
        setShowCreateNewCampaignModal: (show: boolean) => { set(state => { state.showCreateNewCampaignModal = show }) },
        setShowOneClickCampaignModal: (show: boolean) => { set(state => { state.showOneClickCampaignModal = show }) },
        setShowOneClickCampaignSetupModal: (show: boolean) => {
            const { resetCampaignState } = get();
            set(state => {
                state.showOneClickCampaignSetupModal = show
            })
            if (!show) {
                resetCampaignState();
            }
        },
        setNewCampaignAdPlatformFilter: (adPlatform: AdPlatform | null) => { set(state => { state.newCampaignAdPlatformFilter = adPlatform }) },
        setCampaignName: (campaignName: string) => { set(state => { state.campaignName = campaignName }) },
        setNumberOfDays: (numberOfDays: number) => { set(state => { state.numberOfDays = numberOfDays }) },
        setCurrentStep: (step: PlaybookStepName) => { set(state => { state.currentStep = step }) },
        setPlaybookStepNames: (playbookStepNames: PlaybookStepName[]) => { set(state => { state.playbookStepNames = playbookStepNames; }); },
        setExpressSetupOrCustomPages: (value: 'custom' | 'express') => { set(state => { state.expressSetupOrCustomPages = value }) },
        setCallToAction: (callToAction: CallToActionType) => { set(state => { state.callToAction = callToAction }) },
        setSelectedInstagramPosts: (posts: InstagramPost[]) => {
            set(state => {
                state.selectedInstagramPosts = posts
            })
        },
        setImportedFromUrlPosts: (posts: ImportFromURLResponse[]) => {
            set(state => {
                state.importedFromUrlPosts = posts
            })
        },
        removeImportedFromUrlPost: (postId: string) => {
            set(state => {
                state.importedFromUrlPosts = state.importedFromUrlPosts.filter(p => p.id !== postId)
            })
        },
        setUploadedAssets: (assets: CloudinaryUploadedFileType[]) => {
            const { captions } = get();
            let updatedCaptions = captions;
            if (captions.length == 0) {
                updatedCaptions = [{
                    id: 1,
                    text: '',
                    aiGenerated: false
                }];
            }
            set(state => {
                state.uploadedAssets = assets;
                state.captions = updatedCaptions;
            })
        },
        getUploadedAssets: () => {
            return get().uploadedAssets;
        },
        removeUploadedAsset: (assetId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            set(state => { state.uploadedAssets = state.uploadedAssets.filter(a => a.id !== assetId) })

            if (previewedAsset?.id === assetId) {
                setPreviewedAsset(null)
            }
        },
        removeSelectedInstagramPost: (postId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            set(state => { state.selectedInstagramPosts = state.selectedInstagramPosts.filter(p => p.id !== postId) })

            if (previewedAsset?.id === postId) {
                setPreviewedAsset(null)
            }
        },

        setSelectedSparkCodes: (sparkCodes: TikTokSparkAd[]) => { set(state => { state.selectedSparkCodes = sparkCodes }) },
        setPlaybookSteps: (steps: PlaybookStep[]) => {
            set(state => {
                state.playbookStepNames = steps.map(step => step.name)
            })
        },
        setIsLoadingMaestroRecommendations: (isLoading: boolean) => { set(state => { state.isLoadingMaestroRecommendations = isLoading }) },
        addSparkCode: (sparkCode: TikTokSparkAd) => {
            const {
                previewedAsset,
                setPreviewedAsset,
                selectedSparkCodes
            } = get();

            // Check if this spark code already exists in the selectedSparkCodes array
            const sparkId = sparkCode.item_info?.item_id || sparkCode.video_info?.video_id;
            const isDuplicate = selectedSparkCodes.some(code => {
                const existingId = code.item_info?.item_id || code.video_info?.video_id;
                return existingId === sparkId;
            });

            // If it's a duplicate, return false to indicate it wasn't added
            if (isDuplicate) {
                return false;
            }

            let updatePostWithPlatformType: TikTokSparkAd = {
                ...sparkCode,
                platformType: AssetPlatformType.TIKTOK,
            }

            if (!previewedAsset) {
                let transformedSparkCode = transformPostsToCreativeFormat([updatePostWithPlatformType])[0];
                setPreviewedAsset(transformedSparkCode);
            }

            set(state => {
                state.selectedSparkCodes = [...state.selectedSparkCodes, updatePostWithPlatformType];
            });

            // Return true to indicate the spark code was added
            return true;
        },
        removeSparkCode: (sparkCodeId: string) => {
            const {
                previewedAsset,
                setPreviewedAsset
            } = get();

            if (previewedAsset?.id === sparkCodeId) {
                setPreviewedAsset(null);
            }
            set(state => {
                state.selectedSparkCodes = state.selectedSparkCodes.filter(code => code.item_info?.item_id !== sparkCodeId);
            });
        },
        getCampaign: async (campaignId: number) => {
            const {
                campaign: currentCampaign,
                validateGeographyBuckets,
                initializeCampaignState
            } = get();

            if (!campaignId) {
                campaignId = currentCampaign?.id ?? 0;
            }

            if (campaignId === 0) {
                return null;
            }

            set(state => { state.isLoadingCampaign = true });
            try {
                const campaignLocal: CampaignDetails | null = await getCampaign_api(campaignId);
                if (!campaignLocal) {
                    throw new Error('useCampaign::getCampaign::Campaign not found');
                }

                const playbookTypeLocal = campaignLocal.campaign_metadata?.playbook;
                const adPlatformLocal = PLAYBOOK_TYPE_INFO[playbookTypeLocal].adPlatform;

                // Initialize the campaign state with defaults before applying the stored campaign input
                initializeCampaignState({
                    playbookType: playbookTypeLocal
                });

                const campaignMetadata = campaignLocal?.campaign_metadata;
                let v2Input: FacebookCampaignSubmissionType | TikTokCampaignSubmissionType | null = null;

                set(state => { state.adPlatform = adPlatformLocal });
                switch (adPlatformLocal) {
                    case AdPlatform.META:
                        v2Input = campaignMetadata?.v2_campaign_input as FacebookCampaignSubmissionType;
                        break;
                    case AdPlatform.TIKTOK:
                        v2Input = campaignMetadata?.v2_campaign_input as TikTokCampaignSubmissionType;
                        break;
                    // case AD_PLATFORM.YOUTUBE: // NOTE CONNER: this is not used yet, but will be used in the future
                    //     v2Input = campaignMetadata?.v2_campaign_input as YouTubeCampaignSubmissionType;
                    //     break;
                    default:
                        throw new Error(`useCampaign::getCampaign::Unsupported ad platform: ${adPlatformLocal}`);
                }

                if (v2Input) {
                    set(state => {
                        // TypeScript doesn't know v2Input is non-null here, so we need to assert it
                        const v2InputExists = v2Input!;

                        // Common state updates for specific playbook types
                        switch (adPlatformLocal) {
                            case AdPlatform.TIKTOK:
                                const tiktokInput = v2InputExists as TikTokCampaignSubmissionType;
                                state.enabledPlatforms = tiktokInput.enabledPlatforms ?? [];
                                state.selectedSparkCodes = tiktokInput.selectedSparkCodes || [];
                                break;
                            case AdPlatform.META:
                                const fbInput = v2InputExists as FacebookCampaignSubmissionType;
                                state.enabledPlatforms = fbInput.enabledPlatforms ?? [];
                                state.conversionsEnabled = fbInput.conversionsEnabled;
                                state.conversionsEventName = fbInput.conversionsEventName;
                                break;
                        }

                        // Common state updates for all playbook types
                        state.campaign = campaignLocal
                        state.playbookType = v2InputExists.playbookType;
                        state.budget = String(v2InputExists.budget);
                        state.selectedSongToPromote = v2InputExists.record;
                        state.expressSetupOrCustomPages = v2InputExists.usesExpressSetup ? 'express' : 'custom';
                        state.callToAction = v2InputExists.cta;
                        state.currentStep = PlaybookStepName.CREATIVE;

                        // Assets and content
                        if (v2InputExists.assets) {
                            state.uploadedAssets = v2InputExists.assets
                                .filter((asset: CreativeSelectorPost) => asset.originalPostType === AssetPlatformType.CLOUDINARY)
                                .map((asset: CreativeSelectorPost) => ({
                                    id: asset.id,
                                    batchId: '',
                                    asset_id: asset.id,
                                    public_id: asset.id,
                                    version: 1,
                                    version_id: '1',
                                    signature: '',
                                    width: 0,
                                    height: 0,
                                    format: 'mp4',
                                    resource_type: 'video',
                                    created_at: asset.timestamp || '',
                                    bytes: 0,
                                    type: 'upload',
                                    etag: '',
                                    placeholder: false,
                                    url: asset.assetUrl ?? '',
                                    secure_url: asset.assetUrl ?? '',
                                    access_mode: 'public',
                                    original_filename: asset.title,
                                    path: asset.assetUrl ?? '',
                                    thumbnail_url: asset.thumbnailUrl ?? '',
                                    duration: 0,
                                    tags: [],
                                    folder: '',
                                    existing: false,
                                    platformType: AssetPlatformType.CLOUDINARY
                                }));

                            state.selectedInstagramPosts = v2InputExists.assets
                                .filter((asset: CreativeSelectorPost) => asset.originalPostType === AssetPlatformType.INSTAGRAM)
                                .map((asset: CreativeSelectorPost) => ({
                                    id: asset.id,
                                    ig_id: asset.id,
                                    permalink: asset.assetUrl ?? '',
                                    caption: asset.platformCaption || '',
                                    media_product_type: MediaProductType.FEED,
                                    media_type: MediaType.VIDEO,
                                    media_url: asset.assetUrl,
                                    thumbnail_url: asset.thumbnailUrl || '',
                                    timestamp: asset.timestamp || '',
                                    like_count: asset.likesCount || 0,
                                    comments_count: asset.commentsCount || 0,
                                    selectable: true,
                                    platformType: AssetPlatformType.INSTAGRAM
                                }));
                        }

                        state.linksToPromote = v2InputExists.linksToPromote;
                        state.captions = v2InputExists.captions;
                        state.campaignGoal = v2InputExists.campaignGoal || null;
                        // Targeting
                        state.geographyBuckets = v2InputExists.targetedGeographies || [];
                        state.selectedAudienceGroups = Array.isArray(v2InputExists.targetedAudiences)
                            ? v2InputExists.targetedAudiences.map((audience: TargetedAudience) => ({
                                id: audience.id,
                                title: audience.name,
                                tags: Array.isArray(audience.audiences)
                                    ? audience.audiences.map(a => ({
                                        id: Number.isNaN(Number(a.id)) ? 0 : Number(a.id),
                                        title: a.name || '',
                                        type: a.type as "customAudience" | "lookalikeAudience" | "interestTargetingAudience" | "pageAudience" | "presetAudience"
                                    }))
                                    : []
                            }))
                            : [];

                        // Also convert incoming targetedGeographies to geographyBuckets format
                        // This ensures we maintain the proper structure when loading from draft
                        if (Array.isArray(v2InputExists.targetedGeographies) && v2InputExists.targetedGeographies.length > 0) {
                            state.geographyBuckets = v2InputExists.targetedGeographies.map((geo: TargetedGeographyBucket) => ({
                                id: geo.id,
                                name: geo.name,
                                percentage: geo.percentage,
                                geographies: geo.geographies,
                                excludedGeographies: geo.excludedGeographies
                            })) as TargetedGeographyBucket[];
                        }

                        // Demographics
                        const demographics = v2InputExists.targetedDemographics || {};
                        state.minAge = demographics.min_age || 18;
                        state.maxAge = demographics.max_age || 65;
                        state.genders = Array.isArray(demographics.genders)
                            ? demographics.genders.map((g: TargetedGender) => ({
                                key: g.key,
                                name: g.name
                            }))
                            : [];
                        state.languages = Array.isArray(demographics.languages)
                            ? demographics.languages.map((lang: TargetedLanguage) => {
                                if (typeof lang === 'string') {
                                    return {
                                        key: lang,
                                        name: lang,
                                        selected: true
                                    }
                                }
                                return {
                                    key: lang.key,
                                    name: lang.name,
                                    selected: true
                                }
                            })
                            : [];

                        // Budget and dates configuration
                        state.currentBudgetTab = v2InputExists.budgetType || 'lifetime';
                        state.highlightedTrack = v2InputExists.highlightedTrack || null;

                        if (v2InputExists.lengthConfig) {
                            if (v2InputExists.lengthConfig.dateType === 'date_range') {
                                state.currentDateSelectionTab = 'dateRange';

                                const today = dayjs();

                                // Default fallback dates
                                let newStartDate = today;
                                let newEndDate = today.add(7, 'days');

                                // Check if we have valid start and end dates
                                const hasValidStartDate = Boolean(v2InputExists.lengthConfig.startDate);
                                const hasValidEndDate = Boolean(v2InputExists.lengthConfig.endDate);

                                if (hasValidStartDate && hasValidEndDate) {
                                    const inputStartDate = dayjs(v2InputExists.lengthConfig.startDate);
                                    const inputEndDate = dayjs(v2InputExists.lengthConfig.endDate);

                                    // Check if it's a valid time range (start is before end)
                                    if (inputStartDate.isBefore(inputEndDate)) {
                                        // Calculate the original duration
                                        const originalDuration = inputEndDate.diff(inputStartDate);

                                        // Set end date to today + original duration
                                        newEndDate = today.add(originalDuration, 'millisecond');
                                    }
                                }

                                state.startDate = newStartDate.toISOString();
                                state.endDate = newEndDate.toISOString();
                            } else if (v2InputExists.lengthConfig.dateType === 'duration') {
                                state.currentDateSelectionTab = 'numberOfDays';

                                // For duration type, check if start date is in the past
                                const inputStartDate = v2InputExists.lengthConfig.startDate ? dayjs(v2InputExists.lengthConfig.startDate) : dayjs();
                                const today = dayjs();

                                // If start date is in the past, reset to today
                                state.startDate = inputStartDate.isBefore(today) ? today.toISOString() : inputStartDate.toISOString();
                                state.numberOfDays = v2InputExists.lengthConfig?.numberOfDays || 7;
                            } else {
                                // Default to dateRange if dateType is not recognized
                                state.currentDateSelectionTab = 'dateRange';
                                state.startDate = dayjs().toISOString();
                                state.endDate = dayjs().add(7, 'days').toISOString();
                            }
                        } else {
                            // Default values if lengthConfig is missing
                            state.currentDateSelectionTab = 'dateRange';
                            state.startDate = dayjs().toISOString();
                            state.endDate = dayjs().add(7, 'days').toISOString();
                        }
                    });
                }

                validateGeographyBuckets();

                return campaignLocal;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isLoadingCampaign = false });
            }
        },

        createCampaign: async (brandId: string) => {
            const {
                budget,
                startDate,
                endDate,
                playbookType,
            } = get();

            const newCampaignData = {
                budget,
                startDate,
                endDate,
                playbookType,
            };

            try {
                // TODO: make a service for this function
                set(state => { state.isLoadingCampaign = true });
                const res = await Axios.post(`/campaign/brand/${brandId}/create`, newCampaignData);

                const campaignLocal: CampaignDetails = res.data.data;

                set(state => { state.campaign = campaignLocal });

                return campaignLocal;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isLoadingCampaign = false });
            }
        },

        setGeographyBuckets: (geographyBuckets: TargetedGeographyBucket[]) => { set(state => { state.geographyBuckets = geographyBuckets }) },
        setShowEditGeographyBucketsBanner: (show: boolean) => { set(state => { state.showEditGeographyBucketsBanner = show }) },
        validateGeographyBuckets: () => {
            /** Calculates if budget percentages sum to 100% and if all percentages are greater than 0 */
            const { geographyBuckets } = get();
            const total = geographyBuckets.reduce((sum, bucket) => sum + bucket.percentage, 0);
            const isEveryPercentageValid = geographyBuckets.every(bucket => bucket.percentage > 0);
            const everyBucketHasGeographies = geographyBuckets.every(bucket => bucket.geographies.length > 0);
            set(state => { state.isGeographyBucketsObjectValid = total === 100 && isEveryPercentageValid && everyBucketHasGeographies });
        },
        searchGeographies: async (query: string, platform: 'tiktok' | 'facebook'): Promise<TikTokTargetingGeography[] | FBLocation[]> => {
            if (query.length < 2) return [];

            set(state => { state.isLoadingGeographies = true });
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState
            const {
                loggedInFacebookUser
            } = useFacebookConnections.getState()
            try {
                if (platform === 'tiktok') {
                    const locations = await getAvailableTargetingLocations_api(currentBrand.id);
                    if (!locations) return [];

                    // Filter locations based on search query
                    const filteredLocations = locations.filter(location =>
                        location.name.toLowerCase().includes(query.toLowerCase())
                    );

                    return filteredLocations;
                } else {
                    const fbLocations = await searchGeographiesFromFacebook(query, loggedInFacebookUser?.access_token ?? '')
                    return fbLocations.data;
                }
            } catch (error) {
                console.error('Error fetching geographies:', error);
                return [];
            } finally {
                set(state => { state.isLoadingGeographies = false });
            }
        },

        setMinAge: (age: number) => {
            set(state => { state.minAge = age });
        },

        setMaxAge: (age: number) => {
            set(state => { state.maxAge = age });
        },

        setAgeRange: (minAge: number, maxAge: number) => {
            set(state => {
                state.minAge = minAge;
                state.maxAge = maxAge;
            });
        },

        setTargetedGender: (gender: TargetedGender[]) => {
            set(state => { state.genders = gender });
        },

        setLanguages: (locales: TargetedLanguage[]) => {
            set(state => { state.languages = locales });
        },

        setIsDemographicsAutomated: (isAutomated: boolean) => {
            set(state => { state.isDemographicsAutomated = isAutomated });
        },

        setDemographics: (demographics) => {
            set(state => {
                if (demographics.minAge !== undefined) state.minAge = demographics.minAge;
                if (demographics.maxAge !== undefined) state.maxAge = demographics.maxAge;
                if (demographics.genders !== undefined) state.genders = demographics.genders;
                if (demographics.locales !== undefined) state.languages = demographics.locales;
                if (demographics.isAutomated !== undefined) state.isDemographicsAutomated = demographics.isAutomated;
            });
        },

        setCurrentBudgetTab: (tab: CurrentBudgetTabType) => {
            set(state => { state.currentBudgetTab = tab });
        },

        setCurrentDateSelectionTab: (tab: CurrentDateSelectionTabType) => {
            set(state => { state.currentDateSelectionTab = tab });
        },

        setHighlightedTrack: (track: {
            id: string;
            uri: string;
            name: string;
        }) => {
            set(state => { state.highlightedTrack = track });
        },

        setSelectedSongToPromote: (songToPromote: RecordDetails) => {
            let external_links = songToPromote?.external_links
            set(state => {
                state.selectedSongToPromote = songToPromote
                state.linksToPromote = []
                state.enabledPlatforms = external_links?.map(link => ({
                    url: link.url,
                    name: link.name
                })) || []
            });
        },

        getDataForDebugBox: () => {
            const {
                budget,
                startDate,
                endDate,
                campaignGoal: goal,
                currentStep,
                expressSetupOrCustomPages,
                callToAction,
                selectedSparkCodes,
                selectedSongToPromote,
                playbookStepNames,
                linksToPromote,
                enabledPlatforms,
                minAge,
                maxAge,
                genders: gender,
                languages,
                uploadedAssets,
                captions,
                selectedAudienceGroups,
                isDemographicsAutomated,
                geographyBuckets
            } = get();

            return {
                budget,
                startDate,
                endDate,
                goal,
                currentStep,
                expressSetupOrCustomPages,
                callToAction,
                playbookStepNames,
                linksToPromote,
                enabledPlatforms,
                minAge,
                maxAge,
                gender,
                languages,
                selectedSongToPromote,
                uploadedAssets,
                captions,
                selectedAudienceGroups,
                isDemographicsAutomated,
                geographyBuckets
            }
        },

        nextCampaignStep: () => {
            const {
                currentStep,
                playbookStepNames,
                adPlatform,
                playbookType
            } = get();

            const {
                currentBrand
            } = useCurrentBrand.getState() as BrandExistsState;

            if (!currentStep) {
                set(state => { state.currentStep = playbookStepNames[0] });
                return;
            } else {
                const currentStepIndex = playbookStepNames.findIndex(stepName => stepName === currentStep);
                const currentStepName = playbookStepNames[currentStepIndex];

                // Get the next step
                const nextStep = playbookStepNames[currentStepIndex + 1];

                // Check if the next step is the finalization step by name
                const isMovingToFinalization = nextStep && nextStep.includes('FINALIZATION');

                if (!nextStep) {
                    return;
                }

                let analyticsEventTitle = ''
                switch (currentStepName) {
                    case PlaybookStepName.GOAL_SELECTOR:
                    case PlaybookStepName.RECORD_SELECTOR:
                    case PlaybookStepName.LINK_SELECTOR:
                        analyticsEventTitle = 'Campaign Content'
                        break;
                    default:
                        analyticsEventTitle = currentStepName
                        break;
                }

                track(`${analyticsEventTitle} Completed`, {
                    adPlatform,
                    playbookType,
                    brand_id: currentBrand.id,
                    brand_slug: currentBrand.slug,
                    brand_name: currentBrand.name,
                })

                set(state => { state.currentStep = nextStep });

                // If we're moving to the finalization step, scroll to the checkout buttons
                if (isMovingToFinalization) {
                    // Increase timeout to ensure the DOM has fully updated
                    setTimeout(() => {

                        // Try using the global function we added to the component
                        // @ts-ignore
                        if (window.scrollToSubmitButtons && typeof window.scrollToSubmitButtons === 'function') {
                            // @ts-ignore
                            window.scrollToSubmitButtons();
                            return;
                        }

                        // Try the ID first
                        const submitButtonsContainer = document.getElementById('campaignSubmitButtonsContainer');

                        if (submitButtonsContainer) {
                            submitButtonsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                            // Try various fallback selectors
                            const fallbackSelectors = [
                                '.submitCampaignOrUpgradeComponent',
                                '.checkoutWithCardButton',
                                '.submitCampaignButton',
                                '.unlockUnlimitedButton',
                                '.campaignSubmitButtonsContainer',
                                '.submitOrCheckoutButtonsView'
                            ];

                            for (const selector of fallbackSelectors) {
                                const element = document.querySelector(selector);
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    break;
                                }
                            }
                        }
                    }, 500); // Increased timeout to 500ms to ensure DOM is updated
                }
            }
        },

        prevCampaignStep: () => {
            const {
                currentStep,
                playbookStepNames
            } = get();

            if (!currentStep) {
                set(state => { state.currentStep = playbookStepNames[0] });
                return;
            } else {
                const prevStep = playbookStepNames[playbookStepNames.findIndex(stepName => stepName === currentStep) - 1];
                if (prevStep) {
                    set(state => { state.currentStep = prevStep });
                }
            }
        },

        resetDemographicsToRecommended: () => {
            set(state => {
                state.minAge = 18;
                state.maxAge = 35;
                state.genders = FB_DEFAULT_GENDERS;
                state.languages = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
            })
        },

        resetAudiencesToRecommended: () => {
            const { preserveLoadedAudienceGroups } = get();
            set(state => {
                state.selectedAudienceGroups = preserveLoadedAudienceGroups(false);
            })
        },

        resetGeographyBuckets: (multipleLocations?: boolean) => {
            const {
                playbookType,
                adPlatform
            } = get();

            let defaultGeographyBuckets: TargetedGeographyBucket[] = [];

            // First determine the default buckets based on playbook type
            switch (playbookType) {
                case PlaybookType.INCREASE_MUSIC_STREAMS:
                case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                case PlaybookType.META_PRESAVES:
                    defaultGeographyBuckets = [
                        {
                            id: 0,
                            name: 'Touring Markets',
                            percentage: 70,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        },
                        {
                            id: 1,
                            name: 'Global Markets',
                            percentage: 30,
                            geographies: DEFAULT_GLOBAL_MARKET_GEOGRAPHIES
                        }
                    ];
                    break;

                case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                case PlaybookType.TIKTOK_EXTERNAL_TRAFFIC:
                case PlaybookType.TIKTOK_SOUND:
                case PlaybookType.TIKTOK_MUSIC_STREAMS:
                    defaultGeographyBuckets = DEFAULT_TIKTOK_GEOGRAPHIES.map(geography => ({
                        id: geography.id,
                        name: geography.name,
                        percentage: 100,
                        geographies: geography.geographies,
                    }));
                    break;

                case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                case PlaybookType.INCREASE_LINK_CLICKS:
                case PlaybookType.META_TICKET_SALES:
                case PlaybookType.META_EMAIL_SIGNUPS:
                case PlaybookType.META_IG_SOUND:
                case PlaybookType.INCREASE_VIDEO_VIEWS:
                    defaultGeographyBuckets = [{
                        id: 0,
                        name: 'Primary Markets',
                        percentage: 100,
                        geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                    }];
                    break;

                default:
                    // Default to single primary market bucket for any new playbook types
                    defaultGeographyBuckets = [{
                        id: 0,
                        name: 'Primary Markets',
                        percentage: 100,
                        geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                    }];
            }

            // If multipleLocations is false, always reduce to a single bucket
            if (!multipleLocations) {
                // Keep only the first bucket and ensure it has 100% allocation
                defaultGeographyBuckets = [defaultGeographyBuckets[0]];
                defaultGeographyBuckets[0].percentage = 100;
            }

            set(state => {
                state.geographyBuckets = defaultGeographyBuckets;
                // Reset the edit banner when geography buckets are reset
                state.showEditGeographyBucketsBanner = false;
            });
        },

        /**
         * Preserves existing audience groups when initializing campaign state
         * @param preserveExisting - Whether to preserve existing audience groups
         * @returns The audience groups to use (preserved or default)
         */
        preserveLoadedAudienceGroups: (preserveExisting: boolean = true): TargetingAudienceGroup[] => {
            const { selectedAudienceGroups, playbookType, conversionsEnabled } = get();

            const selectedAudienceGroupsLocal = selectedAudienceGroups || [] // NOTE CONNER: most likely dont need this because selectedAudienceGroups is set in the initializeCampaignState function

            // If there are existing audience groups and we should preserve them, return the existing ones
            if (preserveExisting && selectedAudienceGroupsLocal.length > 0) {
                return selectedAudienceGroupsLocal;
            }

            // Otherwise return default audience groups
            const isTikTok = playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT;
            const defaultAudienceGroups: TargetingAudienceGroup[] = [SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS];

            if (!isTikTok) {
                defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.CURRENT_FANS);
            }

            // Add conversion event lookalike audience if conversions are enabled
            if (conversionsEnabled) {
                defaultAudienceGroups.push(SHARED_AUDIENCE_CONFIGS.CONVERSION_EVENT_LOOKALIKES);
            }

            return defaultAudienceGroups;
        },

        initializeCampaignState: ({
            playbookType,
            currentStep,
            playbookStepNames,
        }: {
            playbookType: PlaybookType;
            playbookStepNames?: PlaybookStepName[];
            currentStep?: PlaybookStepName;
        }) => {
            const {
                preserveLoadedAudienceGroups,
                campaignInitialized
            } = get();

            const {
                selectedFacebookPixel
            } = useFacebookConnections.getState();

            try {
                if (!playbookType) {
                    throw new Error('Playbook type is required');
                }

                const adPlatform = PLAYBOOK_TYPE_INFO[playbookType].adPlatform;
                if (!adPlatform) {
                    throw new Error(`Playbook type ${playbookType} doesn't have an associated ad platform`);
                }

                if (playbookStepNames) {
                    set(state => {
                        state.playbookStepNames = playbookStepNames;
                        state.currentStep = currentStep || playbookStepNames[0];
                    });
                }

                set(state => {
                    state.playbookType = playbookType;
                    state.adPlatform = adPlatform;
                });

                if (campaignInitialized) {
                    return;
                }

                // set default values for campaigns here
                let callToActionDefault: CallToActionType;
                let conversionsEventNameDefault: string;
                let conversionsEnabledDefault: boolean;
                let campaignDisclaimerDefault: string;
                let linksToPromoteDefault: ExternalLink[] = [];
                let ageMaxDefault = FB_DEFAULT_MAX_AGE;
                let ageMinDefault = FB_DEFAULT_MIN_AGE;

                let gendersDefault: TargetedGender[] = FB_DEFAULT_GENDERS;
                let languagesDefault: TargetedLanguage[] = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                let geographyBucketsDefault: TargetedGeographyBucket[] = []

                let budgetDefault = '100';
                let startDateDefault = dayjs();
                let endDateDefault = dayjs(startDateDefault).add(7, 'days').toDate();
                let budgetTypeDefault = CURRENT_BUDGET_TABS.LIFETIME
                let audienceGroupsDefault: TargetingAudienceGroup[] = []
                let expressSetupOrCustomPagesDefault = 'custom'

                switch (playbookType) {
                    // Meta/Facebook Playbooks
                    case PlaybookType.INCREASE_MUSIC_STREAMS:
                    case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                        audienceGroupsDefault = preserveLoadedAudienceGroups(false);
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.'
                        languagesDefault = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                        gendersDefault = FB_DEFAULT_GENDERS;
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        callToActionDefault = { value: 'LISTEN_NOW', label: 'Listen Now' };
                        geographyBucketsDefault = [
                            {
                                id: 0,
                                name: 'Touring Markets',
                                percentage: 70,
                                geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                            },
                            {
                                id: 1,
                                name: 'Global Markets',
                                percentage: 30,
                                geographies: DEFAULT_GLOBAL_MARKET_GEOGRAPHIES
                            }
                        ];
                        if (selectedFacebookPixel?.id) {
                            conversionsEnabledDefault = true;
                            conversionsEventNameDefault = DefaultPixelConversionEventNames.StreamingLinkClicked;
                            // Don't add conversion event lookalikes here - let the UI component handle it reactively
                        }
                        break;

                    case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                    case PlaybookType.INCREASE_LINK_CLICKS:
                        audienceGroupsDefault = preserveLoadedAudienceGroups(false);
                        languagesDefault = FB_CAMPAIGNS_DEFAULT_LANGUAGES;
                        gendersDefault = FB_DEFAULT_GENDERS;
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.'
                        linksToPromoteDefault = [{ url: '', name: 'Main Link' }];
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        // For INCREASE_LINK_CLICKS, we might want to check for conversions
                        if (playbookType === PlaybookType.INCREASE_LINK_CLICKS && selectedFacebookPixel?.id) {
                            // Enable site-wide conversions if pixel is available
                            conversionsEnabledDefault = true;
                            conversionsEventNameDefault = DefaultPixelConversionEventNames.LinkClick;
                            // Don't add conversion event lookalikes here - let the UI component handle it reactively
                        }
                        break;

                    case PlaybookType.META_PRESAVES:
                        // Only enable conversions if a Facebook pixel exists
                        conversionsEnabledDefault = Boolean(selectedFacebookPixel?.id);
                        conversionsEventNameDefault = DefaultPixelConversionEventNames.PreSaveSuccess;
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        geographyBucketsDefault = [
                            {
                                id: 0,
                                name: 'Primary Markets',
                                percentage: 100,
                                geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                            }
                        ];
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.';
                        // Don't add conversion event lookalikes here - let the UI component handle it reactively
                        audienceGroupsDefault = [
                            SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS,
                            SHARED_AUDIENCE_CONFIGS.CURRENT_FANS
                        ];
                        break;

                    case PlaybookType.META_TICKET_SALES:
                        // Only enable conversions if a Facebook pixel exists
                        conversionsEnabledDefault = Boolean(selectedFacebookPixel?.id);
                        conversionsEventNameDefault = DefaultPixelConversionEventNames.TourLinkClicked;
                        callToActionDefault = { value: 'BUY_TICKETS', label: 'Buy Tickets' };
                        geographyBucketsDefault = [
                            {
                                id: 0,
                                name: 'Primary Markets',
                                percentage: 100,
                                geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                            }
                        ];
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.';
                        // Don't add conversion event lookalikes here - let the UI component handle it reactively
                        audienceGroupsDefault = [
                            SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS,
                            SHARED_AUDIENCE_CONFIGS.CURRENT_FANS
                        ];
                        break;

                    case PlaybookType.META_EMAIL_SIGNUPS:
                        // Only enable conversions if a Facebook pixel exists
                        conversionsEnabledDefault = Boolean(selectedFacebookPixel?.id);
                        conversionsEventNameDefault = DefaultPixelConversionEventNames.Subscribe;
                        callToActionDefault = { value: 'SIGN_UP', label: 'Sign Up' };
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.';
                        // Don't add conversion event lookalikes here - let the UI component handle it reactively
                        audienceGroupsDefault = [
                            SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS,
                            SHARED_AUDIENCE_CONFIGS.CURRENT_FANS
                        ];
                        break;

                    case PlaybookType.META_IG_SOUND:
                        callToActionDefault = { value: 'WATCH_MORE', label: 'Watch More' };
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        campaignDisclaimerDefault = 'Your campaign will be submitted to Facebook for review. It may take up to 24 hours to go live.';
                        break;

                    // TikTok Playbooks
                    case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                    case PlaybookType.TIKTOK_EXTERNAL_TRAFFIC:
                    case PlaybookType.TIKTOK_SOUND:
                    case PlaybookType.TIKTOK_MUSIC_STREAMS:
                        geographyBucketsDefault = DEFAULT_TIKTOK_GEOGRAPHIES;
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        expressSetupOrCustomPagesDefault = 'express';
                        campaignDisclaimerDefault = 'Your marketing campaign may take up to 48 hours to go live, and you won\'t be able to pause it while it\'s running. You will be redirected to a payment page to finish checkout.';
                        audienceGroupsDefault = [
                            SHARED_AUDIENCE_CONFIGS.SIMILAR_FANS,
                        ];
                        break;

                    // YouTube Playbooks
                    case PlaybookType.INCREASE_VIDEO_VIEWS:
                        campaignDisclaimerDefault = 'Your marketing campaign may take up to 48 hours to go live. You will be redirected to a payment page to finish checkout.';
                        callToActionDefault = { value: 'WATCH_NOW', label: 'Watch Now' };
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        break;

                    // Playlisting Playbooks
                    case PlaybookType.GET_PLAYLISTED:
                        campaignDisclaimerDefault = 'Your campaign will be reviewed by our playlist pitching team for eligibility. Once they\'ve reviewed, it may take up to 7 days to go live as they find relevant playlists. If we are not able to pitch your song effectively, you will be refunded the full amount of your payment with an explanation.';
                        callToActionDefault = { value: 'LISTEN_NOW', label: 'Listen Now' };
                        break;
                    // Default case for any new playbook types
                    default:
                        callToActionDefault = { value: 'LEARN_MORE', label: 'Learn More' };
                        ageMaxDefault = 35;
                        ageMinDefault = 18;
                        geographyBucketsDefault = [{
                            id: 0,
                            name: 'Primary Markets',
                            percentage: 100,
                            geographies: DEFAULT_PRIMARY_MARKET_GEOGRAPHIES
                        }];
                        campaignDisclaimerDefault = 'Your campaign will be submitted for review. It may take up to 24 hours to go live.';
                }

                set(state => {
                    // If we're not preserving data, set all defaults
                    state.minAge = ageMinDefault;
                    state.maxAge = ageMaxDefault;
                    state.callToAction = callToActionDefault;
                    state.linksToPromote = linksToPromoteDefault;
                    state.selectedAudienceGroups = audienceGroupsDefault;
                    state.campaignDisclaimer = campaignDisclaimerDefault;
                    state.geographyBuckets = geographyBucketsDefault;
                    state.languages = languagesDefault;
                    state.genders = gendersDefault;
                    state.budget = budgetDefault;
                    if (adPlatform === AdPlatform.TIKTOK) {
                        state.startDate = dayjs().add(1, 'day').startOf('day').toISOString();
                    } else {
                        state.startDate = startDateDefault.toISOString();
                    }
                    state.endDate = endDateDefault.toISOString();
                    state.currentBudgetTab = budgetTypeDefault;
                    state.expressSetupOrCustomPages = expressSetupOrCustomPagesDefault as 'custom' | 'express';
                    state.campaignInitialized = true;
                    state.conversionsEnabled = conversionsEnabledDefault;
                    state.conversionsEventName = conversionsEventNameDefault;
                });
            } catch (e) {
                console.error('Error initializing campaign state:', e);
            }
        },

        submitCampaign: async ({
            submitType,
            recurring_interval,
            overrideCampaignInput
        }: {
            submitType: 'oneOffCheckout' | 'upgradePlanCheckout' | 'directCreate',
            recurring_interval?: BillingCycle,
            overrideCampaignInput?: YouTubeCampaignData | PlaylistingCampaignInput
        }) => {
            const {
                playbookType,
                getDataForSubmission
            } = get();

            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;

            try {
                set(state => { state.isSubmittingCampaign = true });
                let res: any = null;

                let campaignInput: PlaylistingCampaignInput
                    | TikTokCampaignSubmissionType
                    | YouTubeCampaignData
                    | FacebookCampaignSubmissionType
                    = overrideCampaignInput ?? getDataForSubmission();

                track(`Submit Campaign`, {
                    brand_id: currentBrand.id,
                    brand_slug: currentBrand.slug,
                    brand_name: currentBrand.name,
                    playbook_type: playbookType,
                    submit_type: submitType
                })

                switch (submitType) {
                    case 'directCreate':
                        if (playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
                            res = await createTiktokCampaign_v2_api({
                                brandId: currentBrand.id,
                                campaignInput: campaignInput as TikTokCampaignSubmissionType
                            });
                        } else {
                            res = await createDirectFacebookCampaign_api({
                                brandId: currentBrand.id,
                                campaign_input: campaignInput as FacebookCampaignSubmissionType
                            });
                        }
                        break;
                    case 'upgradePlanCheckout':
                        res = await createUpgradePlanCampaignCheckout_v2_api({
                            campaign_input: campaignInput,
                            recurring_interval: recurring_interval || BillingCycle.MONTHLY,
                            checkout_source: 'upgrade',
                            brandId: currentBrand.id
                        });

                        // Handle Stripe URL redirection if present
                        if (res?.url) {
                            window.location.href = res.url as string;
                            return { redirected: true };
                        }
                        break;
                    case 'oneOffCheckout':
                        res = await createOneOffCampaignCheckout_v2_api({
                            campaign_input: campaignInput,
                            checkout_source: 'campaign-creation',
                            brandId: currentBrand.id
                        });

                        // Handle Stripe URL redirection if present
                        if (res?.url) {
                            window.location.href = res.url as string;
                            return { redirected: true };
                        }
                        break;
                }

                // If response contains a campaign ID, fetch the campaign data
                if ((res as any)?.campaign) {
                    // Set the campaign in the state
                    set(state => { state.campaign = ((res as any).campaign as unknown) as CampaignDetails });
                }

                return res;
            } catch (error) {
                throw error;
            } finally {
                set(state => { state.isSubmittingCampaign = false });
            }
        },

        resetCampaignState: () => {
            set(state => {
                // Reset all campaign state properties
                state.budget = '100'  // Default value is 100
                state.budgetMaximum = '100'  // Default value is 100
                state.startDate = dayjs().toISOString() // Match interface definition (null not empty string)
                state.endDate = dayjs().add(7, 'days').toISOString() // Match interface definition (null not empty string)
                state.playbookType = null
                state.adPlatform = null
                state.campaignGoal = null
                state.campaign = null
                state.showOneClickCampaignModal = false
                state.showOneClickCampaignSetupModal = false
                state.showCreateNewCampaignModal = false
                state.campaignName = ''
                state.isLoadingCampaign = false
                state.isSubmittingCampaign = false
                state.isLoadingSocialItems = false
                state.numberOfDays = 0
                state.selectedInstagramPosts = []
                state.showEditGeographyBucketsBanner = false
                state.captions = []
                state.uploadedAssets = []
                state.enabledPlatforms = []
                state.currentStep = undefined
                state.expressSetupOrCustomPages = 'custom'
                state.callToAction = { value: 'LEARN_MORE', label: 'Learn More' }
                state.selectedSparkCodes = []
                state.campaignInitialized = false
                state.geographyBuckets = [{
                    id: 1,
                    name: '',
                    percentage: 100,
                    geographies: []
                }]
                state.isLoadingGeographies = false
                state.selectedAudienceGroups = []
                state.campaignGoal = null
                state.isGeneratingOneClickCampaign = false
                state.minAge = 18
                state.maxAge = 65
                state.genders = []
                state.languages = []
                state.isDemographicsAutomated = true
                state.previewedAsset = null
                state.currentBudgetTab = CURRENT_BUDGET_TABS.LIFETIME // Default is LIFETIME
                state.currentDateSelectionTab = CURRENT_DATE_SELECTION_TABS.DATE_RANGE
                state.playbookStepNames = []
                state.linksToPromote = []
                state.selectedSongToPromote = null
                state.highlightedTrack = null
                state.isLoadingMaestroRecommendations = false
                state.conversionsEventName = undefined
                state.conversionsEnabled = false
                state.importedFromUrlPosts = []

                // Reset the selected posts
                state.selectedInstagramPost = null
                state.selectedRelease = null
                state.selectedTikTokPost = null
                state.selectedYouTubeVideo = null

                // Clear fetched items
                state.instagramPosts = []
                state.releases = []
                state.tikTokPosts = []
                state.youTubeVideos = []

                // Reset disclaimer
                state.campaignDisclaimer = ''
            })
        },

        // for displaying the posts
        /** Get all selected posts across different types */
        getAllSelectedPosts: (variant: 'facebook' | 'tiktok'): CreativeSelectorPost[] => {
            const {
                selectedInstagramPosts = [],
                uploadedAssets = [],
                selectedSparkCodes = [],
                importedFromUrlPosts = []
            } = get();

            const transformedIGPosts = transformPostsToCreativeFormat(selectedInstagramPosts);
            const transformedUploadedAssets = transformPostsToCreativeFormat(uploadedAssets);
            const transformedSparkPosts = transformPostsToCreativeFormat(selectedSparkCodes);
            const transformedImportedFromUrlPosts = transformPostsToCreativeFormat(importedFromUrlPosts);

            return variant === 'facebook' ?
                [...transformedIGPosts, ...transformedUploadedAssets, ...transformedImportedFromUrlPosts] :
                [...transformedSparkPosts, ...transformedImportedFromUrlPosts];
        },

        getInstagramPosts: async (): Promise<InstagramPost[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getInstagramPosts_api();
                const response = await fakeAsync(1000);
                set(state => { state.instagramPosts = response as InstagramPost[] });
                return response as InstagramPost[];
            } catch (error) {
                console.error('Error fetching Instagram posts:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getReleases: async (): Promise<RecordDetails[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getReleases_api();
                const response = await fakeAsync(1000);
                set(state => { state.releases = response as RecordDetails[] });
                return response as RecordDetails[];
            } catch (error) {
                console.error('Error fetching releases:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getTikTokPosts: async (): Promise<any[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getTikTokPosts_api();
                const response = await fakeAsync(1000);
                set(state => { state.tikTokPosts = response as any[] });
                return response as any[];
            } catch (error) {
                console.error('Error fetching TikTok posts:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        getYouTubeVideos: async (): Promise<any[]> => {
            try {
                set(state => { state.isLoadingSocialItems = true });
                // const response = await getYouTubeVideos_api();
                const response = await fakeAsync(1000);
                set(state => { state.youTubeVideos = response as any[] });
                return response as any[];
            } catch (error) {
                console.error('Error fetching YouTube videos:', error);
                return [];
            } finally {
                set(state => { state.isLoadingSocialItems = false });
            }
        },

        setSelectedInstagramPost: (post: InstagramPost | null) => {
            set(state => { state.selectedInstagramPost = post });
        },

        setSelectedRelease: (release: RecordDetails | null) => {
            set(state => { state.selectedRelease = release });
        },

        setSelectedTikTokPost: (post: any | null) => {
            set(state => { state.selectedTikTokPost = post });
        },

        setSelectedYouTubeVideo: (video: any | null) => {
            set(state => { state.selectedYouTubeVideo = video });
        },

        generateOneClickCampaign: async () => {
            try {
                set(state => { state.isGeneratingOneClickCampaign = true });

                const {
                    getDataForSubmission
                } = get();

                const { currentBrand } = useCurrentBrand.getState() as BrandExistsState

                const campaignInput = getDataForSubmission();

                const response = await getOneClickCampaignDetails_api({
                    brandId: currentBrand?.id,
                    campaignInput: campaignInput as FacebookCampaignSubmissionType
                });
            } catch (error) {
                console.error('Error generating one click campaign:', error);
            } finally {
                set(state => { state.isGeneratingOneClickCampaign = false });
            }
        },


        getDataForSubmission: () => {
            const {
                playbookType,
                adPlatform,
                budget,
                startDate,
                endDate,
                selectedSongToPromote,
                callToAction,
                expressSetupOrCustomPages,
                uploadedAssets,
                selectedInstagramPosts,
                captions,
                selectedAudienceGroups,
                minAge,
                maxAge,
                genders: gender,
                linksToPromote,
                highlightedTrack,
                enabledPlatforms,
                languages,
                currentBudgetTab,
                currentDateSelectionTab,
                conversionsEventName,
                conversionsEnabled,
                geographyBuckets,
                campaignGoal,
                selectedSparkCodes,
                importedFromUrlPosts,
                numberOfDays,
            } = get();

            const {
                selectedFacebookAdAccount,
                selectedFacebookPixel,
                selectedFacebookPage,
                selectedInstagramPage,
            } = useFacebookConnections.getState();

            const {
                tiktokAdAccount
            } = useTikTokConnections.getState();

            // Base submission data matching FacebookCampaignSubmissionType
            const submissionData: Partial<CampaignState> = {
                playbookType,
                selectedSongToPromote,
                expressSetupOrCustomPages,
                callToAction,
                uploadedAssets,
                selectedInstagramPosts,
                importedFromUrlPosts,
                linksToPromote,
                enabledPlatforms,
                captions,
                selectedAudienceGroups,
                budget,
                currentBudgetTab,
                currentDateSelectionTab,
                startDate: startDate || '',
                endDate: endDate || '',
                minAge,
                maxAge,
                genders: gender,
                languages,
                conversionsEventName,
                conversionsEnabled,
                highlightedTrack,
                geographyBuckets,
                campaignGoal,
                selectedSparkCodes,
                numberOfDays,
            };

            const transformedCampaignData = transformDataForCampaignSubmit(adPlatform!, submissionData, {
                selectedFacebookAdAccount: selectedFacebookAdAccount || undefined,
                selectedFacebookPixel: selectedFacebookPixel || undefined,
                selectedFacebookPage: selectedFacebookPage || undefined,
                selectedInstagramPage: selectedInstagramPage || undefined,
                selectedTikTokAdAccount: tiktokAdAccount || undefined,
            });
            return transformedCampaignData;
        },
    }))
);

export default useCampaign;
/**
   * Transforms various post types into a standardized CreativeSelectorPost format
   * @param posts Array of posts to transform (Instagram posts, uploaded assets, or spark posts)
   * @returns Array of transformed and filtered CreativeSelectorPost objects
   */

export const transformPostsToCreativeFormat = (
    posts: (InstagramPost | CloudinaryUploadedFileType | TikTokSparkAd | ImportFromURLResponse)[]
): CreativeSelectorPost[] => {
    if (!posts?.length) return [];

    return posts
        .filter((post) => {
            if (post.platformType === AssetPlatformType.INSTAGRAM) {
                return post.media_url || post.thumbnail_url;
            } else if (post.platformType === AssetPlatformType.CLOUDINARY) {
                return post.url;
            } else if (post.platformType === AssetPlatformType.IMPORTED_FROM_URL) {
                return post.assetUrl;
            } else if (post.item_info || post.platformType === AssetPlatformType.TIKTOK) {
                // For TikTok, ensure we have either a valid item_id or video_id
                return post.item_info?.item_id || post.video_info?.video_id;
            }
        })
        .map((post): CreativeSelectorPost | null => {
            switch (post.platformType) {
                case AssetPlatformType.IMPORTED_FROM_URL: {
                    const importedFromUrlPost = post as ImportFromURLResponse;
                    return {
                        id: importedFromUrlPost.id,
                        title: importedFromUrlPost.platformCaption,
                        timestamp: importedFromUrlPost.timestamp,
                        isRecommended: false,
                        originalPostType: AssetPlatformType.IMPORTED_FROM_URL,
                        mediaType: importedFromUrlPost.mediaType,
                        assetUrl: importedFromUrlPost.assetUrl,
                        thumbnailUrl: importedFromUrlPost.thumbnailUrl,
                        platformCaption: importedFromUrlPost.platformCaption,
                    }
                }
                case AssetPlatformType.TIKTOK: {
                    const tiktokPost = post as TikTokSparkAd;
                    // Generate a consistent ID even if item_id is not available
                    const postId = tiktokPost.item_info!.item_id
                    return {
                        id: postId,
                        title: tiktokPost.user_info?.identity_display_name || tiktokPost.item_info?.item_id || '',
                        timestamp: tiktokPost.auth_info?.auth_start_time || '',
                        isRecommended: false,
                        originalPostType: AssetPlatformType.TIKTOK,
                        mediaType: MediaType.VIDEO,
                        assetUrl: tiktokPost.video_info?.preview_url || '',
                        thumbnailUrl: tiktokPost.video_info?.poster_url || '',
                        platformCaption: tiktokPost.item_info?.text || '',
                        description: `TikTok video from ${tiktokPost.user_info?.identity_display_name || 'creator'}`,
                        tags: [],
                        likesCount: tiktokPost.user_info?.identity_info?.likes_count,
                        commentsCount: 0,
                        sharesCount: 0,
                        backgroundColor: '',
                    };
                }

                case AssetPlatformType.INSTAGRAM: {
                    const instagramPost = post as InstagramPost;
                    // Skip posts without any media
                    if (!instagramPost.media_url && !instagramPost.thumbnail_url) return null;

                    // For videos, prefer thumbnail_url, otherwise use media_url
                    const mediaType = instagramPost.media_type === 'VIDEO' ? MediaType.VIDEO :
                        instagramPost.media_type === 'CAROUSEL_ALBUM' ? MediaType.CAROUSEL_ALBUM :
                            MediaType.IMAGE;

                    const assetUrl = instagramPost.media_url || instagramPost.thumbnail_url;

                    let thumbnailUrl = instagramPost.thumbnail_url || assetUrl;
                    // Skip if we still don't have a valid image URL
                    if (!assetUrl) return null;

                    // Handle carousel children if they exist
                    const children = instagramPost.children?.data?.map((child: {
                        id: string;
                        media_type: string;
                        media_url: string;
                    }) => ({
                        id: child.id,
                        mediaType: child.media_type === 'VIDEO' ? MediaType.VIDEO : MediaType.IMAGE,
                        mediaUrl: child.media_url
                    })) || undefined;

                    return {
                        id: instagramPost.id,
                        title: instagramPost.caption || 'Instagram Post',
                        timestamp: instagramPost.timestamp,
                        isRecommended: false,
                        originalPostType: AssetPlatformType.INSTAGRAM,
                        mediaType: mediaType,
                        assetUrl: assetUrl,
                        ig_id: instagramPost.ig_id || undefined,
                        fb_id: instagramPost.fb_id || undefined,
                        thumbnailUrl: thumbnailUrl,
                        platformCaption: instagramPost.caption,
                        likesCount: instagramPost.like_count,
                        commentsCount: instagramPost.comments_count,
                        children: children,
                        currentCarouselIndex: 0
                    };
                }

                case AssetPlatformType.CLOUDINARY: {
                    // Skip assets without a URL
                    if (!post.url) return null;

                    return {
                        id: post.id,
                        thumbnailUrl: post.thumbnail_url,
                        title: post.original_filename || 'Uploaded Asset',
                        timestamp: post.created_at,
                        isRecommended: false,
                        assetUrl: post.url,
                        mediaType: post.resource_type === 'video' ? MediaType.VIDEO : MediaType.IMAGE,
                        originalPostType: AssetPlatformType.CLOUDINARY,
                    };
                }
            }
        })
        .filter((post): post is CreativeSelectorPost => post !== null);
}
const fakeAsync = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}